import type { Socket } from "socket.io-client"
import { v4 as uuidv4 } from "uuid"
import appStore from "@core/store"
import { getPatientListSnapshot, getVisitElements, getVisitSnapshot, getVisitsByStatus, getVisitSocketAuthTicket } from "@core/api/visitApi"
import type { PatientListName, PatientListScope, PatientListSnapshot } from "@core/api/visitApi"
import { getReservationList, reservationFetchThunk } from "@core/api/reservationApi"
import { waitingVisit, treatmentVisit, completeVisit } from "@/features/visit/visitSlice"
import { applyPatientListUpdates } from "@/features/visit/patientListUpdates"
import { betaSettingsSelector } from "@/features/hospitalSetting/hospitalSettingSelectors"
import type { VisitListSerializer } from "@/types/visit"
import type { ReservationExtendSerializer } from "@/types/reservation"

const visitLists = { waiting: waitingVisit, treatment: treatmentVisit, complete: completeVisit }
type VisitName = keyof typeof visitLists
type Change = PatientListScope & { visitUuids: string[]; reservationUuids: string[] }
type ListEvent = { eventId: string; reason: "changed" | "reception_created" | "reservation_created"; changes: Change[] }
type Pending = { scopes: PatientListScope[]; uuids: Set<string>; full: boolean; first: number; last: number; immediate: boolean; attempts: number }
const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const scopeKey = (scope: PatientListScope) => `${scope.date}:${scope.list}`
const shares = (left: PatientListScope[], right: PatientListScope[]) => left.some(scope => right.some(other => scopeKey(scope) === scopeKey(other)))

export function attachPatientListRefresh(socket: Socket, isEnabled: () => boolean, store = appStore) {
  let alive = true
  let active = false
  let receiving = false
  let epoch = 0
  let connectionGeneration = 0
  let dateGeneration = 0
  let negotiating = false
  let connectAgain = false
  let halted = false
  let reconnects = 0
  let queued = false
  let timer: ReturnType<typeof setTimeout> | null = null
  let pending: Pending[] = []
  let failed: Pending[] = []
  const busy = new Map<string, symbol>()
  const events = new Map<string, number>()
  const date = () => store.getState().selectedDate.selectedDate
  const settings = () => betaSettingsSelector(store.getState())
  const individual = () => settings().betaEnabled && settings().patientListIndividualRefreshEnabled
  const stateFor = (name: PatientListName) => (name === "reservation" ? store.getState().reservation : store.getState()[`${name}Visit`])
  let observedDate = date()
  let observedMode = individual()
  const generation = (scope: PatientListScope) => stateFor(scope.list).generation
  const clearTimer = () => {
    if (timer) clearTimeout(timer)
    timer = null
  }
  const syncAll = () => {
    for (const list of Object.values(visitLists)) store.dispatch(list.fetchList(date()))
    store.dispatch(reservationFetchThunk(date()))
  }
  const due = (group: Pending) => {
    const config = settings()
    if (group.immediate || !config.betaEnabled || !config.delayedRefreshEnabled) return 0
    return Math.min(group.last + config.delayedRefreshDebounceSeconds * 1000, group.first + config.delayedRefreshMaxWaitSeconds * 1000)
  }
  const add = (group: Pending) => {
    let remaining = pending
    let merged = true
    while (merged) {
      merged = false
      remaining = remaining.filter(existing => {
        if (existing.immediate !== group.immediate || !shares(group.scopes, existing.scopes)) return true
        group.scopes = [...new Map([...group.scopes, ...existing.scopes].map(scope => [scopeKey(scope), scope])).values()]
        for (const uuid of existing.uuids) group.uuids.add(uuid)
        group.full ||= existing.full
        group.first = Math.min(group.first, existing.first)
        group.last = Math.max(group.last, existing.last)
        group.attempts = Math.max(group.attempts, existing.attempts)
        merged = true
        return false
      })
    }
    pending = [...remaining, group]
  }
  const query = async (group: Pending): Promise<PatientListSnapshot> => {
    const uuids = [...group.uuids]
    const mode = individual() && !group.full && uuids.length <= 500 ? "elements" : "full"
    if (group.scopes.length > 1) return getPatientListSnapshot(group.scopes, mode, uuids)
    const scope = group.scopes[0]
    if (scope.list === "reservation")
      return { snapshotId: uuidv4(), mode: "full", elements: [], lists: [{ ...scope, rows: await getReservationList(scope.date) }] }
    if (mode === "full") {
      const rows = await (individual() ? getVisitSnapshot(scope.list, scope.date) : getVisitsByStatus(scope.list, scope.date))
      return { snapshotId: uuidv4(), mode, elements: [], lists: [{ ...scope, rows }] }
    }
    const result = await getVisitElements(uuids, scope.date)
    if (result.visitDate !== scope.date) throw new Error("목록 조회 날짜 불일치")
    return { snapshotId: uuidv4(), mode, elements: result.elements, lists: [{ ...scope, order: result.order[scope.list] }] }
  }
  const prepare = (result: PatientListSnapshot, scopes: PatientListScope[]) => {
    if (!result.snapshotId || !Array.isArray(result.lists) || !Array.isArray(result.elements)) throw new Error("묶음 응답 불일치")
    if (scopes.some(scope => !result.lists.some(item => scopeKey(item) === scopeKey(scope)))) throw new Error("묶음 응답 범위 누락")
    const payload: Parameters<typeof applyPatientListUpdates>[0] = { date: date(), visits: {} }
    let missing = false
    for (const list of result.lists) {
      if (list.date !== date() || !scopes.some(scope => scopeKey(scope) === scopeKey(list))) continue
      if (list.list === "reservation") {
        if (!Array.isArray(list.rows)) throw new Error("예약 응답 누락")
        payload.reservations = list.rows as ReservationExtendSerializer[]
      } else {
        let rows: VisitListSerializer[]
        if (result.mode === "full") {
          if (!Array.isArray(list.rows)) throw new Error("전체 응답 누락")
          rows = list.rows as VisitListSerializer[]
        } else {
          if (!Array.isArray(list.order)) throw new Error("목록 순서 누락")
          const known = new Map(store.getState()[`${list.list}Visit`].patients.map(visit => [visit.visitUuid, visit]))
          for (const element of result.elements) {
            known.delete(element.visitUuid)
            if (element.visit?.visitDate === list.date && element.visit.visitStatus === list.list) known.set(element.visitUuid, element.visit)
          }
          missing ||= list.order.some(uuid => !known.has(uuid))
          rows = list.order.filter(uuid => known.has(uuid)).map(uuid => known.get(uuid)!)
        }
        if (rows.some(row => row.visitDate !== list.date || row.visitStatus !== list.list)) throw new Error("목록 요소 범위 불일치")
        payload.visits![list.list] = rows
      }
    }
    const ids = Object.values(payload.visits!).flatMap(rows => rows?.map(row => row.visitUuid) || [])
    if (new Set(ids).size !== ids.length) throw new Error("묶음 응답의 방문 중복")
    return { payload, missing }
  }
  const run = async (group: Pending) => {
    const runDate = date()
    const requestEpoch = epoch
    const requestConnectionGeneration = connectionGeneration
    const requestDateGeneration = dateGeneration
    const visible = group.scopes.filter(scope => scope.date === runDate)
    const generations = visible.map(generation)
    const requestToken = Symbol()
    for (const scope of group.scopes) busy.set(scopeKey(scope), requestToken)
    try {
      let result = await query(group)
      if (!alive || halted || requestEpoch !== epoch || !isEnabled() || date() !== runDate) return
      let prepared = prepare(result, group.scopes)
      if (prepared.missing) {
        group.full = true
        result = await query(group)
        if (!alive || halted || requestEpoch !== epoch || !isEnabled() || date() !== runDate) return
        prepared = prepare(result, group.scopes)
      }
      if (prepared.missing) throw new Error("목록 복구 요소 누락")
      if (
        visible.some((scope, index) => generation(scope) !== generations[index]) ||
        pending.some(
          existing =>
            existing.immediate === group.immediate &&
            shares(group.scopes, existing.scopes) &&
            existing.scopes.some(scope => !group.scopes.some(other => scopeKey(scope) === scopeKey(other)))
        )
      ) {
        add(group)
        return
      }
      store.dispatch(applyPatientListUpdates(prepared.payload))
    } catch (error) {
      if (!alive || halted || requestEpoch !== epoch || !isEnabled()) return
      const status = (error as { response?: { status?: number } })?.response?.status
      if (status === 401 || status === 403) {
        halted = true
        pending = []
        failed = []
      } else if (group.attempts < 1) {
        group.attempts += 1
        group.full = true
        add(group)
      } else if (pending.some(existing => existing.immediate === group.immediate && shares(group.scopes, existing.scopes))) {
        group.full = true
        add(group)
      } else {
        failed.push(group)
        console.warn("[PatientListRefresh] 목록 복구 중단", error)
      }
    } finally {
      for (const scope of group.scopes) if (busy.get(scopeKey(scope)) === requestToken) busy.delete(scopeKey(scope))
      if (
        alive &&
        !halted &&
        isEnabled() &&
        date() === runDate &&
        requestEpoch !== epoch &&
        requestConnectionGeneration === connectionGeneration &&
        requestDateGeneration === dateGeneration &&
        active
      )
        add(group)
      schedule()
    }
  }
  const pump = () => {
    queued = false
    clearTimer()
    if (!alive || !active || halted || !socket.connected || !isEnabled()) return
    let next = Infinity
    for (const group of [...pending].sort((left, right) => due(left) - due(right))) {
      if (group.scopes.some(scope => busy.has(scopeKey(scope)))) continue
      const visible = group.scopes.filter(scope => scope.date === date())
      if (!visible.length) {
        pending = pending.filter(item => item !== group)
        continue
      }
      if (visible.some(scope => stateFor(scope.list).currentRequestId || stateFor(scope.list).loading)) continue
      if (visible.some(scope => scope.list !== "reservation" && store.getState()[`${scope.list}Visit`].authBlocked)) continue
      if (visible.some(scope => scope.list !== "reservation" && !store.getState()[`${scope.list}Visit`].baseValid)) group.full = true
      const wait = due(group) - Date.now()
      if (wait > 0) {
        next = Math.min(next, wait)
        continue
      }
      pending = pending.filter(item => item !== group)
      if (individual() && group.scopes.length === 1 && group.scopes[0].list !== "reservation" && !group.full && group.uuids.size > 200) {
        const remainder = [...group.uuids].slice(200)
        group.uuids = new Set([...group.uuids].slice(0, 200))
        add({ ...group, scopes: [...group.scopes], uuids: new Set(remainder) })
      }
      void run(group)
    }
    if (Number.isFinite(next)) timer = setTimeout(schedule, next)
  }
  const schedule = () => {
    if (!queued && alive) {
      queued = true
      queueMicrotask(pump)
    }
  }
  const changed = (data: ListEvent) => {
    if (!alive || !receiving || !isEnabled() || !uuidPattern.test(data?.eventId || "") || !Array.isArray(data?.changes)) return
    if (!["changed", "reception_created", "reservation_created"].includes(data.reason)) return
    if (
      data.changes.some(
        change =>
          !change ||
          !["waiting", "treatment", "complete", "reservation"].includes(change.list) ||
          !/^\d{4}-\d{2}-\d{2}$/.test(change.date) ||
          !Array.isArray(change.visitUuids) ||
          !Array.isArray(change.reservationUuids) ||
          [...change.visitUuids, ...change.reservationUuids].some(uuid => !uuidPattern.test(uuid))
      )
    )
      return
    const now = Date.now()
    for (const [id, received] of events) if (now - received > 120000) events.delete(id)
    if (events.has(data.eventId)) return
    if (!data.changes.some(change => change.date === date())) return
    const instant = data.changes.filter(
      change =>
        (data.reason === "reception_created" && change.list === "waiting") || (data.reason === "reservation_created" && change.list === "reservation")
    )
    const delayed = data.changes.filter(change => !instant.includes(change))
    const enqueue = (changes: Change[], immediate: boolean) => {
      if (!changes.length || !changes.some(change => change.date === date())) return
      const group: Pending = {
        scopes: changes.map(({ list, date: targetDate }) => ({ list, date: targetDate })),
        uuids: new Set(changes.flatMap(change => change.visitUuids)),
        full: changes.some(change => change.list !== "reservation" && !change.visitUuids.length),
        first: now,
        last: now,
        immediate,
        attempts: 0,
      }
      let recoveryScopes = [...group.scopes]
      let recovered = true
      while (recovered) {
        recovered = false
        failed = failed.filter(existing => {
          if (!shares(recoveryScopes, existing.scopes)) return true
          recoveryScopes = [...new Map([...recoveryScopes, ...existing.scopes].map(scope => [scopeKey(scope), scope])).values()]
          add({ ...existing, attempts: 0 })
          recovered = true
          return false
        })
      }
      if (immediate) {
        for (const existing of pending)
          if (!existing.immediate && shares(group.scopes, existing.scopes)) {
            for (const uuid of existing.uuids) group.uuids.add(uuid)
            group.full ||= existing.full
          }
        pending = pending.filter(
          existing => existing.immediate || !existing.scopes.every(scope => group.scopes.some(other => scopeKey(scope) === scopeKey(other)))
        )
      }
      add(group)
    }
    enqueue(instant, true)
    enqueue(delayed, false)
    events.set(data.eventId, now)
    schedule()
  }
  const connect = async () => {
    if (!alive || !isEnabled() || !socket.id) return
    if (negotiating) {
      connectAgain = true
      return
    }
    negotiating = true
    connectAgain = false
    active = false
    receiving = false
    halted = false
    epoch += 1
    const connectionEpoch = ++connectionGeneration
    const negotiationId = uuidv4()
    let activationRequested = false
    const current = () => alive && connectionGeneration === connectionEpoch && socket.connected && isEnabled()
    const request = async (action: string, authTicket?: string) => {
      const response = await socket
        .timeout(5000)
        .emitWithAck("patient_list_refresh_v1", { action, negotiationId, ...(authTicket ? { authTicket } : {}) })
      if (!current() || !response?.ok || response.negotiationId !== negotiationId) throw new Error("환자목록 협상 확인 실패")
      return response
    }
    try {
      const { ticket } = await getVisitSocketAuthTicket(socket.id, negotiationId)
      if (!current()) return
      if ((await request("prepare", ticket)).mode !== "prepared") throw new Error("환자목록 준비 실패")
      receiving = true
      activationRequested = true
      if ((await request("activate")).mode !== "active") throw new Error("환자목록 활성화 실패")
      active = true
      reconnects = 0
      syncAll()
      schedule()
    } catch (error) {
      if (!current()) return
      console.warn("[PatientListRefresh] 협상 실패", error)
      if (activationRequested) {
        try {
          if ((await request("cancel")).mode !== "cancelled") throw new Error("환자목록 복귀 실패")
        } catch {
          if (!current()) return
          reconnects += 1
          socket.disconnect()
          if (reconnects < 2) socket.connect()
        }
      }
      active = false
      receiving = false
      if (socket.connected) syncAll()
    } finally {
      negotiating = false
      if (connectAgain && alive && socket.connected && isEnabled()) queueMicrotask(() => void connect())
    }
  }
  const disconnect = () => {
    epoch += 1
    connectionGeneration += 1
    active = false
    receiving = false
    pending = []
    failed = []
    busy.clear()
    events.clear()
    clearTimer()
  }
  const unsubscribe = store.subscribe(() => {
    if (date() !== observedDate) {
      observedDate = date()
      epoch += 1
      dateGeneration += 1
      pending = []
      failed = []
      clearTimer()
    }
    if (individual() !== observedMode) {
      observedMode = individual()
      epoch += 1
      if (active && isEnabled()) syncAll()
    }
    schedule()
  })
  socket.on("patient_list_changed_v1", changed)
  socket.on("connect", connect)
  socket.on("disconnect", disconnect)
  return {
    isActive: () => active,
    dispose: () => {
      alive = false
      disconnect()
      unsubscribe()
      socket.off("patient_list_changed_v1", changed)
      socket.off("connect", connect)
      socket.off("disconnect", disconnect)
    },
  }
}
